import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Upload, X, Camera, FileText, User, Building2, 
  CheckCircle, AlertCircle, Clock, Send, Star, Shield
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { collection, addDoc, Timestamp, getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

interface VerificationRequestFormProps {
  suggestion: {
    id: number;
    description: string;
    status: string;
    priority: string;
  };
  structureName: string;
  onBack: () => void;
  onSubmit: (data: any) => void;
}

export function VerificationRequestForm({ suggestion, structureName, onBack, onSubmit }: VerificationRequestFormProps) {
  const [formData, setFormData] = useState({
    completionDate: '',
    implementationDetails: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    additionalNotes: '',
    verificationUrgency: 'standard'
  });

  const [uploadedPhotos, setUploadedPhotos] = useState<Array<{
    id: string;
    file: File | null;
    preview: string;
    description: string;
    category: string;
  }>>([]);

  const [dragActive, setDragActive] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Calculate form completion percentage
  const calculateCompletion = () => {
    const requiredFields = [
      formData.completionDate,
      formData.implementationDetails,
      formData.contactPerson,
      formData.contactPhone,
      formData.contactEmail
    ];
    const completed = requiredFields.filter(field => field.trim() !== '').length;
    const photosCompleted = uploadedPhotos.length >= 2 ? 1 : 0;
    return Math.round(((completed + photosCompleted) / (requiredFields.length + 1)) * 100);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };

  const handleFiles = (files: File[]) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    imageFiles.forEach(file => {
      const id = Math.random().toString(36).substr(2, 9);
      const preview = URL.createObjectURL(file);
      
      setUploadedPhotos(prev => [...prev, {
        id,
        file,
        preview,
        description: '',
        category: 'before-after'
      }]);
    });
  };

  const removePhoto = (id: string) => {
    setUploadedPhotos(prev => {
      const photo = prev.find(p => p.id === id);
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== id);
    });
  };

  const updatePhotoData = (id: string, field: string, value: string) => {
    setUploadedPhotos(prev => prev.map(photo => 
      photo.id === id ? { ...photo, [field]: value } : photo
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark as submitted to show "verifica in corso" badge
    setIsSubmitted(true);
    
    const submissionData = {
      suggestionId: suggestion.id,
      formData,
      photos: uploadedPhotos.map(photo => ({
        id: photo.id,
        description: photo.description,
        category: photo.category,
        fileName: photo.file?.name || ''
      })),
      submittedAt: new Date().toISOString(),
      status: 'pending-verification'
    };

    // Save to Firestore (primary)
    saveVerificationRequestToFirestore(submissionData, structureName);

    // Try to send email (secondary, non-blocking)
    sendVerificationEmailSilent(submissionData, structureName);

    // Show success message to user
    toast.success('✓ Abbiamo preso in carico la tua richiesta', {
      description: 'Riceverai una notifica non appena la verifica sarà completata',
      duration: 5000
    });

    // Proceed with the callback
    onSubmit(submissionData);
  };

  /**
   * Save verification request to Firestore
   * This is the primary data store - the email is optional
   */
  const saveVerificationRequestToFirestore = async (submissionData: any, structureName: string) => {
    try {
      const db = getFirestore();
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        return;
      }

      // If we have uploaded photos, convert them to base64 for storage
      const photosForStorage: any[] = [];
      for (const photo of uploadedPhotos) {
        if (photo.file) {
          const base64 = await fileToBase64(photo.file);
          photosForStorage.push({
            fileName: photo.file.name,
            base64: base64,
            description: photo.description,
            category: photo.category,
            mimeType: photo.file.type,
            size: photo.file.size
          });
        }
      }

      // Save verification request to Firestore (note: hyphen in collection name)
      const verificationRequestsRef = collection(db, 'verification-requests');
      await addDoc(verificationRequestsRef, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        structureName,
        suggestion,
        formData: submissionData.formData,
        photos: photosForStorage,
        submittedAt: Timestamp.now(),
        status: 'pending-verification',
        emailSent: false,
        createdAt: Timestamp.now()
      });
    } catch (error) {
      // Silently ignore errors - user is already notified of success
    }
  };

  /**
   * Send verification email to cto@travelin.me with all request details and images
   * This is non-critical - errors are logged but not shown to the user
   * The data is already safely stored in Firestore
   */
  const sendVerificationEmailSilent = async (submissionData: any, structureName: string) => {
    try {
      // Convert images to base64
      const photosWithBase64: any[] = [];
      for (const photo of uploadedPhotos) {
        if (photo.file) {
          const base64 = await fileToBase64(photo.file);
          photosWithBase64.push({
            fileName: photo.file.name,
            base64: base64,
            description: photo.description,
            category: photo.category,
            mimeType: photo.file.type
          });
        }
      }

      const emailPayload = {
        to: 'cto@travelin.me',
        subject: `[TRAVELIN] Nuova Richiesta di Verifica Accessibilità - ${structureName}`,
        structureName,
        suggestion,
        formData: submissionData.formData,
        photos: photosWithBase64,
        submittedAt: submissionData.submittedAt
      };

      // Try to send via Firebase Cloud Function
      // This is best-effort - if it fails, the data is already in Firestore
      const cloudFunctionUrl = import.meta.env.VITE_VERIFICATION_FUNCTION_URL || 
        'https://us-central1-travelinauth.cloudfunctions.net/sendVerificationEmail';
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        const response = await fetch(
          cloudFunctionUrl,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailPayload),
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        // Email sent (no need to log)
      } catch (fetchError) {
        clearTimeout(timeoutId);
        // Silently ignore fetch errors
      }
    } catch (error) {
      // Silently ignore any other errors
      // The important data is already in Firestore
    }
  };

  /**
   * Convert file to base64 string
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 part after comma
        resolve(result.split(',')[1] || result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const photoCategories = [
    { value: 'before-after', label: 'Confronto Prima/Dopo' },
    { value: 'implementation', label: 'Processo di Implementazione' },
    { value: 'final-result', label: 'Risultato Finale' },
    { value: 'accessibility-feature', label: 'Funzione di Accessibilità' },
    { value: 'signage', label: 'Segnaletica e Orientamento' },
    { value: 'equipment', label: 'Attrezzature e Strumenti' },
    { value: 'documentation', label: 'Documentazione' }
  ];

  const completionPercentage = calculateCompletion();
  const canSubmit = uploadedPhotos.length >= 2 && formData.implementationDetails && formData.contactPerson && formData.contactPhone && formData.contactEmail && formData.completionDate;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Enhanced Header */}
      <div className="bg-gradient-travelin border-b border-blue-200/30 shadow-xl">
        <div className="max-w-6xl mx-auto">
          {/* Top section with back button */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={onBack}
                className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 shadow-lg"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna ai Miglioramenti
              </Button>
              <div className="text-white">
                <h1 className="text-xxl font-medium">Richiedi Verifica Accessibilità</h1>
                <p className="text-s text-blue-100 mt-1">{structureName}</p>
                <div className="mt-1 flex items-center gap-2 text-ss text-white/80">
                  <span>Powered by</span>
                  <img
                    src="/travelin-logo.svg"
                    alt="Travelin"
                    className="h-4 w-auto rounded-sm bg-white px-1 py-0.5"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Progress section */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-white">
                <h2 className="text-l font-medium mb-1">Completa la Tua Richiesta di Verifica</h2>
                <p className="text-blue-100">Fornisci documentazione per verificare il tuo miglioramento di accessibilità</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right text-white">
                  <div className="text-xxl font-bold">{completionPercentage}%</div>
                  <div className="text-s text-blue-100">Completato</div>
                </div>
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center relative">
                    <Shield className="w-6 h-6 text-white" />
                    {completionPercentage === 100 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-white/20 rounded-full h-2">
              <div 
                className="bg-white rounded-full h-2 transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Improvement Details - Enhanced */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-blue-50/30">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-travelin flex items-center justify-center shadow-lg">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Intervento da Verificare</CardTitle>
                    <CardDescription className="text-m mt-1">
                      Fornisci documentazione completa per questo miglioramento di accessibilità
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className={`shadow-sm ${
                  isSubmitted
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white'
                }`}>
                  <Star className="w-3 h-3 mr-1" />
                  {isSubmitted ? 'Verifica in corso' : 'Richiesta Prioritaria'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-ss bg-white shadow-sm">
                      ID: {suggestion.id}
                    </Badge>
                    <Badge variant="outline" className={`text-ss bg-white shadow-sm ${
                      suggestion.priority === 'high' 
                        ? 'border-red-200 text-red-700' 
                        : suggestion.priority === 'medium'
                        ? 'border-yellow-200 text-yellow-700'
                        : 'border-gray-200 text-gray-700'
                    }`}>
                      {suggestion.priority} priority
                    </Badge>
                  </div>
                </div>
                <p className="text-m leading-relaxed text-gray-800 font-medium">{suggestion.description}</p>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Form Progress Indicator */}
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Modulo di Richiesta di Verifica</h2>
                  <div className="text-s text-muted-foreground">
                    Completa tutte le sezioni per inviare la tua richiesta
                  </div>
                </div>
                
                {/* Step Indicator */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { 
                      step: 1, 
                      title: "Dettagli dell'Implementazione", 
                      completed: formData.completionDate && formData.implementationDetails,
                      icon: FileText 
                    },
                    { 
                      step: 2, 
                      title: "Informazioni di Contatto", 
                      completed: formData.contactPerson && formData.contactPhone && formData.contactEmail,
                      icon: User 
                    },
                    { 
                      step: 3, 
                      title: "Documentazione Fotografica", 
                      completed: uploadedPhotos.length >= 2,
                      icon: Camera 
                    }
                  ].map((item, index) => (
                    <div key={item.step} className="flex items-center">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                        item.completed 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-gray-300 bg-white text-gray-500'
                      }`}>
                        {item.completed ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <item.icon className="w-4 h-4" />
                        )}
                      </div>
                      <div className="ml-3 flex-1">
                        <p className={`text-s font-medium ${
                          item.completed ? 'text-green-700' : 'text-gray-900'
                        }`}>
                          Passaggio {item.step}
                        </p>
                        <p className={`text-ss ${
                          item.completed ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {item.title}
                        </p>
                      </div>
                      {index < 2 && (
                        <div className={`w-12 h-0.5 mx-2 ${
                          item.completed ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Step 1: Implementation Details */}
            <Card className="border-0 shadow-xl bg-white relative">
              {/* Step Number Badge */}
              <div className="absolute -top-4 left-6 z-10">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  formData.completionDate && formData.implementationDetails
                    ? 'bg-green-500 border-green-500 text-white shadow-lg' 
                    : 'bg-gradient-travelin border-white text-white shadow-lg'
                }`}>
                  {formData.completionDate && formData.implementationDetails ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <span className="text-s font-bold">1</span>
                  )}
                </div>
              </div>
              
              <CardHeader className="pb-4 pt-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-travelin flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-l">Passaggio 1: Dettagli dell'Implementazione</CardTitle>
                    <CardDescription className="text-m">
                      Raccontaci esattamente come hai completato questo miglioramento
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 p-6 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="completionDate" className="text-m font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Data di Completamento *
                    </Label>
                    <Input
                      id="completionDate"
                      type="date"
                      value={formData.completionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, completionDate: e.target.value }))}
                      required
                      className={`h-12 text-m border-2 ${
                        formData.completionDate ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="verificationUrgency" className="text-m font-medium">Timeline di Verifica</Label>
                    <Select value={formData.verificationUrgency} onValueChange={(value) => setFormData(prev => ({ ...prev, verificationUrgency: value }))}>
                      <SelectTrigger className="h-12 text-m">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            Standard (7-14 giorni)
                          </div>
                        </SelectItem>
                        <SelectItem value="urgent">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            Urgente (2-3 giorni)
                          </div>
                        </SelectItem>
                        <SelectItem value="emergency">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            Emergenza (24 ore)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="implementationDetails" className="text-m font-medium">Dettagli dell'Implementazione *</Label>
                  <Textarea
                    id="implementationDetails"
                    placeholder="Fornisci una descrizione dettagliata di come è stato implementato il miglioramento. Includi i materiali utilizzati, i metodi impiegati, le sfide superate e le funzioni di accessibilità specifiche aggiunte..."
                    value={formData.implementationDetails}
                    onChange={(e) => setFormData(prev => ({ ...prev, implementationDetails: e.target.value }))}
                    rows={5}
                    required
                    className={`text-m resize-none border-2 ${
                      formData.implementationDetails ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                    }`}
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-s text-muted-foreground">
                      {formData.implementationDetails.length}/500 caratteri consigliati
                    </p>
                    {formData.implementationDetails && (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-s font-medium">Completato</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="additionalNotes" className="text-m font-medium">Note Aggiuntive</Label>
                  <Textarea
                    id="additionalNotes"
                    placeholder="Qualsiasi informazione aggiuntiva che potrebbe essere rilevante per il team di verifica..."
                    value={formData.additionalNotes}
                    onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                    rows={3}
                    className="text-m resize-none"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Contact Information */}
            <Card className="border-0 shadow-xl bg-white relative">
              {/* Step Number Badge */}
              <div className="absolute -top-4 left-6 z-10">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  formData.contactPerson && formData.contactPhone && formData.contactEmail
                    ? 'bg-green-500 border-green-500 text-white shadow-lg' 
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 border-white text-white shadow-lg'
                }`}>
                  {formData.contactPerson && formData.contactPhone && formData.contactEmail ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <span className="text-s font-bold">2</span>
                  )}
                </div>
              </div>
              
              <CardHeader className="pb-4 pt-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-l">Passaggio 2: Informazioni di Contatto</CardTitle>
                    <CardDescription className="text-m">
                      Contatto principale per verifica e domande di follow-up
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 p-6 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="contactPerson" className="text-m font-medium">Persona di Contatto *</Label>
                    <Input
                      id="contactPerson"
                      placeholder="Nome completo"
                      value={formData.contactPerson}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
                      required
                      className={`h-12 text-m border-2 ${
                        formData.contactPerson ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="contactPhone" className="text-m font-medium">Numero di Telefono *</Label>
                    <Input
                      id="contactPhone"
                      type="tel"
                      placeholder="+39 000 000 0000"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                      required
                      className={`h-12 text-m border-2 ${
                        formData.contactPhone ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="contactEmail" className="text-m font-medium">Indirizzo Email *</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      placeholder="contact@example.com"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                      required
                      className={`h-12 text-m border-2 ${
                        formData.contactEmail ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                      }`}
                    />
                  </div>
                </div>
                
                {/* Completion Indicator */}
                {formData.contactPerson && formData.contactPhone && formData.contactEmail && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-s font-medium">Informazioni di contatto complete</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 3: Photo Documentation */}
            <Card className="border-0 shadow-xl bg-white relative">
              {/* Step Number Badge */}
              <div className="absolute -top-4 left-6 z-10">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  uploadedPhotos.length >= 2
                    ? 'bg-green-500 border-green-500 text-white shadow-lg' 
                    : 'bg-gradient-travelin-warm border-white text-white shadow-lg'
                }`}>
                  {uploadedPhotos.length >= 2 ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <span className="text-s font-bold">3</span>
                  )}
                </div>
              </div>
              
              <CardHeader className="pb-4 pt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-travelin-warm flex items-center justify-center shadow-lg">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Passaggio 3: Documentazione Fotografica</CardTitle>
                      <CardDescription className="text-m">
                        La prova visiva è essenziale per la verifica (minimo 2 foto richieste)
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className={`shadow-sm ${
                    uploadedPhotos.length >= 2 
                      ? 'bg-green-50 border-green-200 text-green-700' 
                      : 'bg-white'
                  }`}>
                    {uploadedPhotos.length}/10 Foto
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 p-6 pt-0">
                {/* Enhanced Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${
                    dragActive 
                      ? 'border-orange-400 bg-orange-50 scale-[1.02]' 
                      : uploadedPhotos.length >= 2
                      ? 'border-green-300 bg-green-50/50'
                      : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
                      uploadedPhotos.length >= 2 
                        ? 'bg-green-100' 
                        : 'bg-gradient-travelin-warm'
                    }`}>
                      {uploadedPhotos.length >= 2 ? (
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      ) : (
                        <Upload className="w-10 h-10 text-white" />
                      )}
                    </div>
                    <h3 className="text-xl font-medium mb-2">
                      {uploadedPhotos.length >= 2 ? 'Fantastico! Puoi aggiungere altre foto' : 'Carica le Tue Foto'}
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md">
                      {uploadedPhotos.length >= 2 
                        ? 'Hai abbastanza foto per inviare. Aggiungine altre per una verifica completa.'
                        : 'Trascina e rilascia le tue foto qui, o fai clic per selezionare i file. Le immagini di alta qualità aiutano ad accelerare la verifica.'
                      }
                    </p>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileInput}
                      className="hidden"
                      id="photo-upload"
                    />
                    <Button 
                      type="button" 
                      size="lg"
                      className="bg-gradient-travelin-warm hover:bg-gradient-travelin-warm/90 text-white shadow-lg"
                      onClick={() => document.getElementById('photo-upload')?.click()}
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Seleziona Foto
                    </Button>
                    <p className="text-ss text-muted-foreground mt-4">
                      Supportati: JPG, PNG, HEIC, WebP • Max 10MB ciascuno • Fino a 10 foto
                    </p>
                  </div>
                </div>

                {/* Uploaded Photos Grid - Enhanced */}
                {uploadedPhotos.length > 0 && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-l font-medium">Foto Caricate ({uploadedPhotos.length})</h4>
                      {uploadedPhotos.length >= 2 && (
                        <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Pronto per l'Invio
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {uploadedPhotos.map((photo) => (
                        <div key={photo.id} className="border border-gray-200 rounded-xl p-4 space-y-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                          <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                            <ImageWithFallback
                              src={photo.preview}
                              alt="Uploaded verification photo"
                              className="w-full h-full object-cover"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="absolute top-2 right-2 h-8 w-8 p-0 shadow-lg"
                              onClick={() => removePhoto(photo.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="text-s font-medium">Categoria Foto</Label>
                              <Select 
                                value={photo.category} 
                                onValueChange={(value) => updatePhotoData(photo.id, 'category', value)}
                              >
                                <SelectTrigger className="h-10 mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {photoCategories.map((cat) => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                      {cat.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label className="text-s font-medium">Descrizione</Label>
                              <Textarea
                                placeholder="Descrivi cosa mostra questa foto..."
                                value={photo.description}
                                onChange={(e) => updatePhotoData(photo.id, 'description', e.target.value)}
                                rows={2}
                                className="text-s mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadedPhotos.length < 2 && (
                  <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                    <div>
                      <p className="text-s font-medium text-yellow-800">
                        Foto Richieste per la Verifica
                      </p>
                      <p className="text-s text-yellow-700">
                        Carica almeno 2 foto che mostrino il miglioramento completato. Questo aiuta il nostro team di verifica a valutare il tuo lavoro in modo rapido e accurato.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Enhanced Submission Section */}
            <Card className="border-0 shadow-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <CardContent className="p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                      <Send className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium mb-2">Pronto a Inviare la Tua Richiesta di Verifica?</h3>
                      <p className="text-blue-100 text-m">
                        Il nostro team di verifica rivedrà la tua presentazione e ti risponderà entro il periodo di tempo selezionato.
                      </p>
                      {!canSubmit && (
                        <div className="mt-3 flex items-center gap-2 text-yellow-200">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-s">Completa tutte le sezioni obbligatorie sopra</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={onBack}
                      className="bg-white/10 border-white text-white hover:bg-white/20 h-12 px-6"
                    >
                      Annulla
                    </Button>
                    <Button 
                      type="submit" 
                      size="lg"
                      className="bg-white text-blue-600 hover:bg-gray-50 shadow-lg h-12 px-8 font-medium"
                      disabled={!canSubmit}
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Invia per Verifica
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </div>
  );
}